import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertType, Spinner } from '../../components';
import { ACTIVE_TAB_PARAM, AppRoutes, GONG_SUCCESS_PARAM, SALESLOFT_SUCCESS_PARAM } from '../../constants';
import { useToast } from '../../hooks';
import { ComponentSize, IntegrationService, SettingsTab } from '../../types';
import { capitalizeFirstLetter, labelToKebabCase } from '../../utils';

const IntegrationsCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { showToast } = useToast();

  // This is used to prevent the toast from showing multiple times.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success') === 'true';

    // Determine integration type based on the current path
    const integrationType =
      location.pathname === AppRoutes.GONG_CALLBACK ? IntegrationService.GONG : IntegrationService.SALESLOFT;

    // Create new search params for the redirect
    const redirectParams = new URLSearchParams();

    // Set service-specific success parameter and active tab according to the integration type
    switch (integrationType) {
      case IntegrationService.GONG:
        redirectParams.set(GONG_SUCCESS_PARAM, success.toString());
        redirectParams.set(ACTIVE_TAB_PARAM, labelToKebabCase(IntegrationService.GONG));
        break;
      case IntegrationService.SALESLOFT:
        redirectParams.set(SALESLOFT_SUCCESS_PARAM, success.toString());
        redirectParams.set(ACTIVE_TAB_PARAM, labelToKebabCase(IntegrationService.SALESLOFT));
        break;
      default:
        return;
    }

    // Navigate to integrations page with corresponding tab open
    navigate({
      pathname: AppRoutes.SETTINGS + '/' + SettingsTab.INTEGRATIONS,
      search: redirectParams.toString(),
    });

    // Show appropriate success/error messages based on integration type
    if (success) {
      showToast({
        message: `Successfully connected to ${capitalizeFirstLetter(integrationType)}`,
        type: AlertType.SUCCESS,
      });
    } else {
      const errorDescription = searchParams.get('error_description');
      showToast({
        title: `Failed to connect to ${capitalizeFirstLetter(integrationType)}`,
        message: errorDescription ?? '',
        type: AlertType.ERROR,
      });
    }
  }, [navigate, showToast, location]);

  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner size={ComponentSize.MEDIUM} />
    </div>
  );
};

export default IntegrationsCallbackPage;
